import type { Deal } from "@/api/client";
import { Button } from "@/components/ui/button";
import { FeedCard } from "@/components/ui/feed-card";
import { DealStatusBadge } from "./DealStatusBadge";

interface Props {
  deal: Deal;
  onResume: () => void;
  onDiscard: () => void;
  isDiscarding?: boolean;
  /**
   * Non-destructive [X]: stamps draft_dismissed_at so the card stops
   * rendering. Unlike Discard, the draft itself survives (still resumable
   * from the wizard until it expires).
   */
  onDismiss: () => void;
}

function timeRemaining(expiresAt: string | null): string | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return "Expired";
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return `${days}d ${hours % 24}h remaining`;
  }
  return `${hours}h ${minutes}m remaining`;
}

/**
 * Resumable-draft feed card. Built on the shared `FeedCard` primitive
 * (CHP-523): drafts are low-priority, so the lane is neutral. The client name
 * (with its draft status badge) is the title, the merged wizard-position +
 * time-left + no-hold caveat line is the detail (CHP-1030), and Resume /
 * Discard are the actions.
 */
export function DraftResumeCard({ deal, onResume, onDiscard, isDiscarding, onDismiss }: Props) {
  const remaining = timeRemaining(deal.draft_expires_at);
  // CHP-1030: show the RESUME position, matching where the wizard reopens
  // (resolveInitialStep) — last_completed_stage is server data-depth (1=name
  // only, 2=client details filled, 3=opportunity filled), clamped to [1, 3]:
  // stage 2 → "Step 2 of 3".
  const resumeStep =
    deal.last_completed_stage != null ? Math.min(Math.max(deal.last_completed_stage, 1), 3) : null;
  const progress = [resumeStep != null ? `Step ${resumeStep} of 3` : null, remaining]
    .filter(Boolean)
    .join(" · ");

  return (
    // CHP-407: the whole card is clickable to resume the draft (its primary
    // action). The [X] dismiss and the Discard button stop propagation so they
    // don't also trigger the resume.
    <FeedCard
      tone="neutral"
      // CHP-1030: designer eyebrow. Markup stays sentence-case for screen
      // readers; FeedCard's eyebrow style uppercases it (RACCPE-17).
      eyebrow="Draft deal registration expiring"
      title={
        <span className="flex items-center gap-2">
          <span>{deal.client_name}</span>
          <DealStatusBadge status={deal.deal_status} />
        </span>
      }
      description={
        <p className="text-xs text-muted-foreground">
          {progress ? `${progress}. ` : ""}This draft does not hold or secure the opportunity.
        </p>
      }
      onClick={onResume}
      ariaLabel={`Resume draft for ${deal.client_name}`}
      onDismiss={onDismiss}
      dismissLabel={`Dismiss notification for ${deal.client_name}`}
      action={
        <div className="flex gap-2">
          <Button
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onResume();
            }}
          >
            Resume form
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onDiscard();
            }}
            disabled={isDiscarding}
          >
            {isDiscarding ? "Discarding..." : "Discard"}
          </Button>
        </div>
      }
    />
  );
}
